import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineEllipsisVertical, HiStar, HiOutlineUserGroup } from 'react-icons/hi2';
import {
  SidebarContainer,
  SectionHeader,
  SidebarContent,
  CollapseIcon,
  ConnectionIndicator,
  AddChatButton,
  ChatMenuDropdown,
  ChatMenuItem,
  ChatItem,
  ChatsLoadingSpinner,
  ChatListItemRow,
  ChatListItemMain,
  ChatListItemTop,
  ChatListItemMeta,
  ChatListItemPreview,
  ChatListItemTime,
  ChatUnreadDot,
  ChatListItemAvatarWrap,
  ChatListItemAvatar,
  ChatListItemAvatarImage,
  ChatListItemAvatarStatus,
  ChatListItemName,
} from '../../styles/UserWorkspacePageStyles';
import { formatChatListTime, getAvatarColor, getFirstTwoLetters } from '../../common';
import { techColors } from '../../styles/TechRetroEffects';
import { useT } from '../../LocaleContext';

const FAVORITES_STAR_SIZE = 18;

const renderChatTrailing = ({ lastMessageTime, isUnread }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
    {lastMessageTime && (
      <ChatListItemTime $unread={isUnread}>{formatChatListTime(lastMessageTime)}</ChatListItemTime>
    )}
    {isUnread && <ChatUnreadDot aria-hidden />}
  </div>
);

const renderChatAvatar = ({
  chat,
  user,
  userAvatarUrl,
  peerAvatars,
  chatTrustPolicies,
  groupFallback,
}) => {
  if (chat.peer_name === 'favorites') {
    return (
      <ChatListItemAvatarWrap>
        <ChatListItemAvatar $favorites>
          <HiStar
            style={{
              width: `${FAVORITES_STAR_SIZE}px`,
              height: `${FAVORITES_STAR_SIZE}px`,
              color: '#ffffff',
              fill: '#ffffff',
              flexShrink: 0,
            }}
          />
        </ChatListItemAvatar>
      </ChatListItemAvatarWrap>
    );
  }

  if (chat.chat_type === 'group') {
    const label = chat.title || groupFallback;
    return (
      <ChatListItemAvatarWrap>
        <ChatListItemAvatar color={getAvatarColor(label)}>
          <HiOutlineUserGroup style={{ width: '18px', height: '18px' }} />
        </ChatListItemAvatar>
      </ChatListItemAvatarWrap>
    );
  }

  const trustPolicy = chatTrustPolicies?.[chat.id];
  const shareStatus = trustPolicy?.shareStatus;
  const userOnline = chat.user_online === 'true';
  const isUnknown = !shareStatus;
  const avatarUrl = trustPolicy?.shareAvatar ? peerAvatars?.[chat.peer_connect_id] : null;
  const initials = getFirstTwoLetters(chat.peer_name || '?');

  return (
    <ChatListItemAvatarWrap>
      <ChatListItemAvatar color={getAvatarColor(chat.peer_connect_id || chat.peer_name || '')}>
        {avatarUrl ? (
          <ChatListItemAvatarImage src={avatarUrl} alt={chat.peer_name} />
        ) : (
          initials
        )}
      </ChatListItemAvatar>
      <ChatListItemAvatarStatus>
        <ConnectionIndicator isConnected={userOnline} isUnknown={isUnknown} />
      </ChatListItemAvatarStatus>
    </ChatListItemAvatarWrap>
  );
};

const ChatSidebar = ({
  chats,
  onSelectChat,
  selectedChatId,
  isServiceConnected,
  blinkingChats = [],
  unreadChatIds = [],
  isCollapsed,
  onToggleCollapse,
  heightPercent,
  fillRemaining = false,
  isResizing,
  onAddChatClick,
  chatMenuRef,
  isChatMenuOpen,
  onOpenChatMenu,
  onMenuSendRequest,
  onMenuCreateGroup,
  onMenuChatRequests,
  onMenuGroupInvites,
  groupInvitesCount = 0,
  onDeleteChat,
  onTrustPeer,
  onOpenGroupInfo,
  openChatMenuId,
  onOpenChatItemMenu,
  chatItemMenuRefs,
  chatTrustPolicies,
  isLoadingChats,
  incomingRequestsCount = 0,
  user,
  userAvatarUrl,
  peerAvatars = {},
}) => {
  const t = useT();
  const [menuPositions, setMenuPositions] = useState({});
  const portalMenuRef = React.useRef(null);
  const groupFallback = t('chat.group');

  useEffect(() => {
    if (!openChatMenuId) {
      return;
    }

    const updatePosition = () => {
      const menuRef = chatItemMenuRefs.current[openChatMenuId];
      if (menuRef) {
        const rect = menuRef.getBoundingClientRect();
        setMenuPositions(prev => ({
          ...prev,
          [openChatMenuId]: {
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
          }
        }));
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [openChatMenuId, chatItemMenuRefs]);

  const renderChatMenu = (chatId) => {
    if (openChatMenuId !== chatId || !menuPositions[chatId]) {
      return null;
    }

    const position = menuPositions[chatId];
    const chat = chats.find(c => c.id === chatId);
    if (!chat) return null;

    const isGroup = chat.chat_type === 'group';

    return createPortal(
      <div
        ref={portalMenuRef}
        data-portal-chat-menu
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          right: `${position.right}px`,
          zIndex: 10000,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <ChatMenuDropdown isOpen={true}>
          {isGroup && onOpenGroupInfo && (
            <ChatMenuItem
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenGroupInfo(chat.id);
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {t('sidebar.groupInfo')}
            </ChatMenuItem>
          )}
          {!isGroup && (
            <ChatMenuItem 
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTrustPeer(e, chat);
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {t('sidebar.trustUser')}
            </ChatMenuItem>
          )}
          <ChatMenuItem 
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDeleteChat(e, chat);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            $danger
          >
            {isGroup ? t('sidebar.deleteGroup') : t('sidebar.deleteChat')}
          </ChatMenuItem>
        </ChatMenuDropdown>
      </div>,
      document.body
    );
  };

  const chatsMenuLabel = (groupInvitesCount > 0 || incomingRequestsCount > 0)
    ? `${t('sidebar.chatsMenu')} — ${t('modals.pending')}`
    : t('sidebar.chatsMenu');

  return (
    <SidebarContainer heightPercent={heightPercent} isResizing={isResizing} $fillRemaining={fillRemaining}>
      <SectionHeader>
        <CollapseIcon onClick={onToggleCollapse}>
          {isCollapsed ? <HiOutlineChevronDown /> : <HiOutlineChevronUp />}
        </CollapseIcon>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flex: 1 }}>
          <h3>{t('sidebar.chats')}</h3>
          <ConnectionIndicator isConnected={isServiceConnected} />
        </div>
        <div style={{ position: 'relative' }} ref={chatMenuRef}>
          <AddChatButton
            onClick={onOpenChatMenu}
            $blink={!isChatMenuOpen && (groupInvitesCount > 0 || incomingRequestsCount > 0)}
            aria-label={chatsMenuLabel}
          >
            <HiOutlineEllipsisVertical style={{ width: '18px', height: '18px' }} />
          </AddChatButton>
          <ChatMenuDropdown isOpen={isChatMenuOpen}>
            <ChatMenuItem onClick={onMenuSendRequest}>
              {t('sidebar.createChat')}
            </ChatMenuItem>
            <ChatMenuItem onClick={onMenuCreateGroup}>
              {t('sidebar.createGroup')}
            </ChatMenuItem>
            <ChatMenuItem onClick={onMenuChatRequests}>
              {t('sidebar.chatInvites')}{incomingRequestsCount > 0 ? ` (${incomingRequestsCount})` : ''}
            </ChatMenuItem>
            <ChatMenuItem onClick={onMenuGroupInvites}>
              {t('sidebar.groupInvites')}{groupInvitesCount > 0 ? ` (${groupInvitesCount})` : ''}
            </ChatMenuItem>
          </ChatMenuDropdown>
        </div>
      </SectionHeader>
      {!isCollapsed && (
        <SidebarContent>
          {isLoadingChats ? (
            <ChatsLoadingSpinner />
          ) : chats.length > 0 ? (
            chats.map((chat) => {
              const chatId = String(chat.id);
              const isBlinking = blinkingChats.some((id) => String(id) === chatId);
              const isUnread = unreadChatIds.some((id) => String(id) === chatId);
              const isFavorites = chat.peer_name === 'favorites';
              const isGroup = chat.chat_type === 'group';
              const preview = chat.last_message_preview;

              return (
                <ChatItem
                  key={chat.id}
                  isSelected={chat.id === selectedChatId}
                  isBlinking={isBlinking}
                  isUnread={isUnread}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectChat(chat.id);
                  }}
                >
                  <ChatListItemRow>
                    {renderChatAvatar({
                      chat,
                      user,
                      userAvatarUrl,
                      peerAvatars,
                      chatTrustPolicies,
                      groupFallback,
                    })}
                    <ChatListItemMain>
                      {isFavorites ? (
                        <ChatListItemTop>
                          <ChatListItemName>{t('sidebar.favorites')}</ChatListItemName>
                        </ChatListItemTop>
                      ) : isGroup ? (
                        <>
                          <ChatListItemTop>
                            <ChatListItemName $unread={isUnread}>
                              {chat.title || groupFallback}
                            </ChatListItemName>
                            {renderChatTrailing({
                              lastMessageTime: chat.last_message_time,
                              isUnread,
                            })}
                          </ChatListItemTop>
                          {preview ? (
                            <ChatListItemPreview $unread={isUnread}>{preview}</ChatListItemPreview>
                          ) : (
                            <ChatListItemMeta>
                              {t('chat.membersCount', { count: chat.member_count || 0 })}
                            </ChatListItemMeta>
                          )}
                        </>
                      ) : (
                        <>
                          <ChatListItemTop>
                            <ChatListItemName $unread={isUnread}>
                              {chat.peer_name}
                            </ChatListItemName>
                            {renderChatTrailing({
                              lastMessageTime: chat.last_message_time,
                              isUnread,
                            })}
                          </ChatListItemTop>
                          {preview && (
                            <ChatListItemPreview $unread={isUnread}>{preview}</ChatListItemPreview>
                          )}
                        </>
                      )}
                    </ChatListItemMain>
                    {!isFavorites && (
                      <div style={{ position: 'relative' }} ref={el => chatItemMenuRefs.current[chat.id] = el}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenChatItemMenu(e, chat.id);
                          }}
                          style={{
                            color: techColors.neonCyan,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s ease',
                            opacity: 0.6
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = '1';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = '0.6';
                            e.target.style.transform = 'scale(1)';
                          }}
                          title={t('sidebar.chatsMenu')}
                        >
                          <HiOutlineEllipsisVertical style={{ width: '16px', height: '16px' }} />
                        </div>
                        {renderChatMenu(chat.id)}
                      </div>
                    )}
                  </ChatListItemRow>
                </ChatItem>
              );
            })
          ) : (
            <p>{t('sidebar.noChats')}</p>
          )}
        </SidebarContent>
      )}
    </SidebarContainer>
  );
};

export default ChatSidebar;
